import { supabase } from '../lib/supabase';
import { RRConnectionStatus, RRRental, CrgGenerationResult } from '../types';

/**
 * Service RentalReady : toutes les requêtes passent par des edge functions
 * (les tokens et secrets OAuth ne quittent jamais le serveur).
 */

// Extrait le message d'erreur JSON renvoyé par l'edge function (FunctionsHttpError)
const extractError = async (error: any): Promise<string> => {
  try {
    if (error?.context && typeof error.context.json === 'function') {
      const body = await error.context.json();
      if (body?.error) return body.error;
    }
  } catch { /* ignore */ }
  return error?.message || 'Erreur inconnue';
};

const invokeRR = async <T>(functionName: string, body: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.functions.invoke(functionName, { body });
  if (error) {
    throw new Error(await extractError(error));
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return data as T;
};

export const rentalReadyService = {
  /**
   * Enregistre les identifiants OAuth RentalReady d'une agence (admin/superadmin).
   */
  saveCredentials: async (agencyId: string, clientId: string, clientSecret: string): Promise<void> => {
    await invokeRR('rr-save-credentials', { agencyId, clientId, clientSecret });
  },

  /**
   * Statut de connexion RentalReady d'une agence (jamais de secrets).
   */
  getConnectionStatus: async (agencyId: string): Promise<RRConnectionStatus> => {
    return invokeRR<RRConnectionStatus>('rr-connection-status', { agencyId });
  },

  /**
   * Démarre le flux OAuth : retourne l'URL d'autorisation RentalReady à ouvrir.
   */
  startOAuth: async (agencyId: string): Promise<string> => {
    const { authUrl } = await invokeRR<{ authUrl: string }>('rr-oauth-init', { agencyId });
    return authUrl;
  },

  /**
   * Liste les rentals RentalReady de l'agence (pour lier un bien en gestion).
   */
  listRentals: async (agencyId: string): Promise<RRRental[]> => {
    const { rentals } = await invokeRR<{ rentals: RRRental[] }>('rr-rentals', { agencyId });
    return rentals || [];
  },

  /**
   * Génère (ou régénère) les brouillons de CRG d'un mois pour les biens donnés.
   */
  generateCrg: async (
    agencyId: string,
    year: number,
    month: number,
    propertyIds?: string[]
  ): Promise<CrgGenerationResult[]> => {
    const { results } = await invokeRR<{ results: CrgGenerationResult[] }>('rr-generate-crg', {
      agencyId,
      year,
      month,
      propertyIds,
    });
    return results || [];
  },
};
